import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { fetchLeads, fetchStats, downloadLeadsCsv, adminDeleteLead, adminBulkDeleteLeads } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Logo } from "@/components/landing/Logo";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  Download, Search, LogOut, Users, TrendingUp, Calendar, Mail,
  ChevronLeft, ChevronRight, Loader2, ExternalLink, ArrowLeft, LayoutDashboard, Trash2,
} from "lucide-react";

const PLANS = ["Starter", "Growth", "Enterprise"];

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl bg-white border border-slate-200 p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="mt-3 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
      {value}
    </div>
  </div>
);

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function AdminLeads() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { ids, single }
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, leads] = await Promise.all([
        fetchStats(),
        fetchLeads({
          page,
          page_size: 20,
          q: q || undefined,
          plan: plan === "all" ? undefined : plan,
        }),
      ]);
      setStats(s);
      setData(leads);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [page, q, plan]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const onLogout = () => {
    logout();
    toast.success("Signed out");
    nav("/admin/login");
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadLeadsCsv();
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 20)));

  // Selection
  const allOnPage = useMemo(() => data.items.map((l) => l.id), [data.items]);
  const allSelectedOnPage = allOnPage.length > 0 && allOnPage.every((id) => selected.has(id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelectedOnPage) allOnPage.forEach((id) => next.delete(id));
    else allOnPage.forEach((id) => next.add(id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const clearSelection = () => setSelected(new Set());

  const confirmDeleteOne = (lead) => setConfirm({ ids: [lead.id], single: true, name: lead.name });
  const confirmDeleteBulk = () => setConfirm({ ids: Array.from(selected), single: false });

  const performDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      if (confirm.single) {
        await adminDeleteLead(confirm.ids[0]);
        toast.success("Lead deleted");
      } else {
        const res = await adminBulkDeleteLeads(confirm.ids);
        toast.success(`${res.deleted ?? confirm.ids.length} leads deleted`);
      }
      clearSelection();
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-leads-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={()=>nav("/admin")} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Logo />
            <span className="hidden sm:inline-flex items-center text-xs font-bold px-2 py-1 rounded-full bg-blue-50 text-blue-700">
              Leads
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
              data-testid="admin-view-site"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </a>
            <Button variant="outline" size="sm" onClick={()=>nav("/admin")} className="rounded-full" data-testid="goto-dashboard">
              <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Dashboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nav("/admin/cms")}
              data-testid="admin-open-cms"
              className="rounded-full"
            >
              Open CMS
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Leads dashboard
            </h1>
            <p className="text-slate-600 mt-1">Manage demo requests captured from the website. Signed in as <b>{user?.email}</b></p>
          </div>
          <Button
            onClick={onExport}
            disabled={exporting}
            data-testid="export-csv-btn"
            className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="stats-grid">
          <StatCard icon={Users} label="Total leads" value={stats?.total_leads ?? "—"} accent="bg-blue-100 text-blue-700" />
          <StatCard icon={Calendar} label="Today" value={stats?.today ?? "—"} accent="bg-emerald-100 text-emerald-700" />
          <StatCard icon={TrendingUp} label="This week" value={stats?.this_week ?? "—"} accent="bg-amber-100 text-amber-700" />
          <StatCard icon={Mail} label="Subscribers" value={stats?.newsletter_subscribers ?? "—"} accent="bg-purple-100 text-purple-700" />
        </div>

        {stats?.by_plan && Object.keys(stats.by_plan).length > 0 && (
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Leads by plan interest</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(stats.by_plan).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-sm">
                  <span className="font-semibold text-slate-900">{k}</span>
                  <span className="text-xs font-bold text-emerald-700">{v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search by name, email, company, phone…"
              data-testid="leads-search-input"
              className="pl-9 rounded-full bg-white"
            />
          </div>
          <Select
            value={plan}
            onValueChange={(v) => { setPlan(v); setPage(1); }}
          >
            <SelectTrigger className="w-full sm:w-[200px] rounded-full bg-white" data-testid="plan-filter">
              <SelectValue placeholder="All plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              {PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {selected.size > 0 && (
            <div className="flex items-center justify-between px-5 py-2.5 bg-blue-50 border-b border-blue-100" data-testid="bulk-actions-bar">
              <div className="text-sm font-semibold text-blue-900">
                {selected.size} selected
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={clearSelection} className="text-blue-700 hover:bg-blue-100" data-testid="bulk-clear-btn">
                  Clear
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={confirmDeleteBulk}
                  className="rounded-full"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete {selected.size}
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={toggleAll}
                      aria-label="Select all"
                      data-testid="select-all-checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Company</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Received</th>
                  <th className="px-5 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-500" data-testid="leads-empty">
                    No leads match your filters.
                  </td></tr>
                )}
                {!loading && data.items.map((l) => (
                  <tr key={l.id} className={`hover:bg-slate-50 ${selected.has(l.id) ? "bg-blue-50/40" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.has(l.id)}
                        onChange={() => toggleOne(l.id)}
                        aria-label={`Select lead ${l.name}`}
                        data-testid={`lead-checkbox-${l.id}`}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-900">{l.name}</div>
                      {l.message && <div className="text-xs text-slate-500 max-w-xs truncate" title={l.message}>{l.message}</div>}
                    </td>
                    <td className="px-5 py-4 text-slate-700">{l.company}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${l.email}`} className="text-blue-700 hover:underline">{l.email}</a>
                    </td>
                    <td className="px-5 py-4 text-slate-700 whitespace-nowrap">{l.phone}</td>
                    <td className="px-5 py-4">
                      {l.plan ? (
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{l.plan}</span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        onClick={() => confirmDeleteOne(l)}
                        title="Delete lead"
                        aria-label={`Delete lead ${l.name}`}
                        data-testid={`lead-delete-${l.id}`}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
              <div className="text-xs text-slate-600" data-testid="pagination-info">
                Showing <b>{(data.page - 1) * data.page_size + 1}</b>–
                <b>{Math.min(data.page * data.page_size, data.total)}</b> of <b>{data.total}</b>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="page-prev"
                  className="rounded-full h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-xs font-semibold text-slate-700">
                  {data.page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={data.page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="page-next"
                  className="rounded-full h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.single ? "Delete this lead?" : `Delete ${confirm?.ids?.length || 0} leads?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.single ? (
                <>This will permanently delete <b>{confirm.name}</b>. This action cannot be undone.</>
              ) : (
                <>This will permanently delete <b>{confirm?.ids?.length}</b> selected leads. This action cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              disabled={deleting}
              data-testid="lead-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
